"use client"

import { useMemo, useState, type ReactNode } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "refrigerator-lifespan-calculator",
  "cooking-temperature-converter",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function ApplianceEnergyUsagePageClient({ children }: { children?: ReactNode }) {
  const meta = TOOL_METADATA["appliance-energy-calculator"]

  const [applianceType, setApplianceType] = useState("refrigerator")
  const [size, setSize] = useState("medium")
  const [age, setAge] = useState("medium")
  const [electricityRate, setElectricityRate] = useState("13")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const baseUsage: Record<string, Record<string, number>> = {
      refrigerator: { small: 350, medium: 500, large: 700 },
      freezer: { small: 300, medium: 450, large: 600 },
      "wine-cooler": { small: 200, medium: 300, large: 400 },
      compact: { small: 250, medium: 300, large: 350 },
    }
    let kwhPerYear = baseUsage[applianceType]?.[size] || 500
    if (age === "old") kwhPerYear *= 1.4
    else if (age === "medium") kwhPerYear *= 1.15
    // Bounds unchanged; out-of-range input is rejected rather than clamped —
    // `safeParseNumber(electricityRate, 1, 100)` returned 1 for a typed -16.5,
    // which is truthy, so the guard could not fire and every cost below was
    // computed at 1c/kWh. FRIDGE-384 / FRIDGE-411.
    const rate = readQuantity(electricityRate, { min: 1, max: 100 })
    if (rate === null) return null
    const yearlyCost = (kwhPerYear * rate) / 100
    const monthlyCost = yearlyCost / 12
    let comparison = "Your appliance is energy efficient"
    if (age === "old") comparison = "Uses 40% more energy than a new model"
    else if (age === "medium") comparison = "Uses 15% more energy than a new model"
    return {
      kwhPerYear: Math.round(kwhPerYear),
      yearlyCost,
      monthlyCost,
      comparison,
    }
  }, [applianceType, size, age, electricityRate])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="What does your"
      heroTitleItalic="appliance"
      heroTitlePost="really cost to run?"
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~30 sec", "DOE EnergyGuide methodology"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "500", label: "kWh / yr baseline", note: "DOE avg fridge" },
            { value: "+40%", label: "Pre-2000 units", note: "vs new" },
            { value: "$0.13", label: "US avg rate", note: "EIA 2024" },
            { value: "10-15%", label: "Energy Star saves", note: "vs standard" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "appliance-energy-calculator")}
      cta={{
        title: "Ready to swap that energy hog for an Energy Star model?",
        body: "Browse Energy Star-certified refrigerators sorted by annual kWh consumption.",
        buttonLabel: "Browse Energy Star fridges",
        buttonHref: "/collections/energy-star-appliances",
      }}
    >
      {/* CALCULATOR */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Four inputs.
                <br />
                One real bill.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Department of Energy baseline kWh, adjusted for unit age and your local rate.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label htmlFor="appliance-energy-type" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">Appliance type</label>
                  <select
                    id="appliance-energy-type"
                    value={applianceType}
                    onChange={(e) => setApplianceType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="refrigerator">Full-size refrigerator</option>
                    <option value="freezer">Standalone freezer</option>
                    <option value="wine-cooler">Wine cooler</option>
                    <option value="compact">Compact / mini fridge</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="appliance-energy-size" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">Size</label>
                  <select
                    id="appliance-energy-size"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="small">Small (under 18 cu ft)</option>
                    <option value="medium">Medium (18–25 cu ft)</option>
                    <option value="large">Large (25+ cu ft)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="appliance-energy-age" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">Age</label>
                  <select
                    id="appliance-energy-age"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="new">New (0–3 years)</option>
                    <option value="medium">Medium (4–8 years)</option>
                    <option value="old">Old (9+ years)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="appliance-energy-rate" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Electricity rate (¢ / kWh)
                  </label>
                  <div className="relative">
                    <input
                      id="appliance-energy-rate"
                      type="number"
                      step="0.01"
                      value={electricityRate}
                      onChange={(e) => setElectricityRate(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">¢/kWh</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> US average is 13¢ / kWh (EIA, 2024).
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Estimated annual cost
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {`$${result?.yearlyCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">/ yr</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.comparison ?? "Enter details to see annual cost."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Annual kWh</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.kwhPerYear ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Monthly cost</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{`$${result?.monthlyCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "—"}`}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FIRST CAROUSEL */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Drop your bill"
          title="Counter-depth refrigerators to compare"
          description="A new full-size fridge typically uses 400–500 kWh a year, against 800+ for one made before 2000. The ENERGY STAR badge marks certified models."
          collectionType="counter-depth"
          fallbackCollectionType="top-freezer"
        />
      </section>

      {/* METHODOLOGY */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                DOE numbers. <em className="italic text-[#ff9951]">Real</em> rates.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Base kWh comes from Department of Energy EnergyGuide testing; rate uses the EIA US residential average of 13¢/kWh.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Baseline kWh by type & size", d: "Refrigerators 350–700 kWh/yr; freezers 300–600; wine coolers 200–400. From DOE EnergyGuide test data." },
                { n: "02", t: "Age adjustment", d: "Old (9+ yrs): ×1.4. Medium (4–8): ×1.15. New (0–3): ×1.0. Reflects compressor and insulation degradation." },
                { n: "03", t: "Cost = kWh × rate", d: "Annual cost = annual kWh × your rate ÷ 100. Monthly = annual ÷ 12." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* SECOND CAROUSEL */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges — the efficiency leaders"
          description="Top-freezer is the most efficient style by configuration. Often beats French door by 100+ kWh/yr."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
      {/* Server-rendered supporting data belongs inside the shell, before its footer. */}
      {children}
    </ToolPageShell>
  )
}
